import { inject, Injectable } from '@angular/core';
import { AuthService, LocalStorageService } from '@boels/helpers';
import {
  createCart,
  loadCart,
  setActiveCart,
  setCheckoutAccount,
} from '../../cart/actions/cart.action';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  throwError,
} from 'rxjs';
import {
  BoelsBaseSite,
  BoelsUser,
  CartUrlEnum,
  CartUrlValueEnum,
  CartUser,
  LocalCartDataType,
  StorageKey,
  UserEnum,
} from '@boels/models';
import { HttpHeaders } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { CartState } from '../../cart/reducers/cart.reducer';
import { UserDataFacade } from '../../user-data/facade/user-data-facade';
import { SiteContextFacade } from '../../site-context/facade/site-context.facade';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class CartManagementService {
  // DI Injection
  #occAuthService = inject(AuthService);
  #store: Store<CartState> = inject(Store);
  #siteContextFacade = inject(SiteContextFacade);
  #localStorageService = inject(LocalStorageService);
  #userDataFacade = inject(UserDataFacade);
  // Properties
  readonly #cartUserSubject: ReplaySubject<CartUser> = new ReplaySubject(1);
  readonly #cartGuidSubject: ReplaySubject<string | undefined> =
    new ReplaySubject(1);
  readonly #cartKeyPrefix = 'CART_' as const;

  constructor() {
    this.#setCartOnStartup().pipe(takeUntilDestroyed()).subscribe();
  }

  #setCartOnStartup(): Observable<boolean> {
    return combineLatest([
      this.#siteContextFacade.getActiveBaseSiteEntity(),
      this.#userDataFacade.userData(),
    ]).pipe(
      filter(
        ([baseSite, user]) =>
          !!baseSite &&
          (user.type !== UserEnum.B2C_AUTHENTICATED || !!user.debtor),
      ),
      switchMap(([baseSite, user]) => {
        const cart = this.#getStoredCart(baseSite);
        return this.#handleCartOnStartup(user, cart);
      }),
      catchError(() => {
        return EMPTY;
      }),
    );
  }

  #handleCartOnStartup(
    user: BoelsUser,
    localCart: LocalCartDataType | undefined,
  ): Observable<boolean> {
    if (user.type === UserEnum.B2C_AUTHENTICATED && user.b2cToken) {
      return this.#handleB2CCart(user.b2cToken, localCart);
    }
    return this.#handleAnonymousCart(localCart);
  }

  #handleB2CCart(
    b2cToken: string,
    localCartData: LocalCartDataType | undefined,
  ): Observable<boolean> {
    const cartUser: CartUser = {
      type: UserEnum.B2C_AUTHENTICATED,
      b2cToken: b2cToken,
    };
    this.#cartUserSubject.next(cartUser);
    this.#store.dispatch(
      setCheckoutAccount({ checkoutAccount: UserEnum.B2C_AUTHENTICATED }),
    );
    return this.#loadOrCreateCart(UserEnum.B2C_AUTHENTICATED, localCartData);
  }

  #handleAnonymousCart(
    localCartData: LocalCartDataType | undefined,
  ): Observable<boolean> {
    if (localCartData?.user_type === UserEnum.GUEST) {
      return this.#handleGuestCart(localCartData);
    }

    const cartUser: CartUser = {
      type: UserEnum.ANONYMOUS,
    };
    this.#cartUserSubject.next(cartUser);
    return this.#loadOrCreateCart(UserEnum.ANONYMOUS, localCartData);
  }

  #handleGuestCart(localCartData: LocalCartDataType): Observable<boolean> {
    const cartUser: CartUser = {
      type: UserEnum.GUEST,
    };
    this.#cartUserSubject.next(cartUser);
    return this.#loadOrCreateCart(UserEnum.GUEST, localCartData);
  }

  #getStoredCart(baseSite?: BoelsBaseSite): LocalCartDataType | undefined {
    const localCart = this.#localStorageService.getItem(
      (this.#cartKeyPrefix + baseSite?.uid) as StorageKey,
    );

    if (!localCart) return undefined;

    let localCartData: LocalCartDataType;
    if (typeof localCart === 'string')
      localCartData = {
        cartId: localCart,
        user_type: UserEnum.ANONYMOUS,
      };
    else localCartData = localCart as LocalCartDataType;

    return localCartData;
  }

  #loadOrCreateCart(
    currentUser: UserEnum,
    localCartData?: LocalCartDataType,
  ): Observable<boolean> {
    if (!localCartData) {
      this.#cartGuidSubject.next(undefined);
      const isAnonymous = currentUser === UserEnum.ANONYMOUS;
      this.#store.dispatch(
        isAnonymous ? setActiveCart({ activeCartId: '' }) : loadCart(),
      );

      return of(true);
    } else {
      const isRegisteredUser = currentUser === UserEnum.B2C_AUTHENTICATED;
      switch (localCartData.user_type) {
        case UserEnum.ANONYMOUS:
          this.#cartGuidSubject.next(localCartData.cartId);
          this.#store.dispatch(isRegisteredUser ? createCart() : loadCart());
          break;
        case UserEnum.GUEST:
          if (isRegisteredUser) this.#cartGuidSubject.next(undefined);
          else this.#cartGuidSubject.next(localCartData.cartId);
          this.#store.dispatch(loadCart());
          break;
        default:
          throwError(() => new Error('Unknown cart user type'));
      }

      return of(true);
    }
  }

  public getCartUser(): Observable<CartUser> {
    return this.#cartUserSubject.asObservable();
  }

  public getCartId(): Observable<string | undefined> {
    return this.#cartGuidSubject.asObservable();
  }

  public setCartId(cartUser: UserEnum, cartId: string): void {
    if (cartUser === UserEnum.B2C_AUTHENTICATED) {
      this.#cartGuidSubject.next(undefined);
      return;
    }

    this.#cartGuidSubject.next(cartId);
  }

  public switchAnonymousToGuestCart(baseSite: BoelsBaseSite) {
    //TODO: Uncomment when backend is ready
    // const cartUser: CartUser = {
    //   type: UserEnum.GUEST,
    //   authToken: authToken,
    // };
    // this._cartUserSubject.next(cartUser);
    const localCart = this.#getStoredCart(baseSite);
    if (localCart && localCart.user_type === UserEnum.ANONYMOUS) {
      this.storeLocalCart(baseSite, UserEnum.GUEST, localCart.cartId);
    }
  }

  public getAuthToken() {
    return this.#occAuthService.getToken();
  }

  public getCartUrl(
    userType: UserEnum,
    urlType: CartUrlEnum,
  ): CartUrlValueEnum {
    switch (urlType) {
      case CartUrlEnum.ORDERS:
        return userType !== UserEnum.B2C_AUTHENTICATED
          ? CartUrlValueEnum.ANONYMOUS_ORDER
          : CartUrlValueEnum.REGISTERED_ORDER;
      case CartUrlEnum.ADDRESS:
        return userType !== UserEnum.B2C_AUTHENTICATED
          ? CartUrlValueEnum.ANONYMOUS_ADRESS
          : CartUrlValueEnum.REGISTERED_ADRESS;
      case CartUrlEnum.CREATE:
        return userType !== UserEnum.B2C_AUTHENTICATED
          ? CartUrlValueEnum.ANONYMOUS_CART
          : CartUrlValueEnum.REGISTERED_CART;
      default:
        return userType !== UserEnum.B2C_AUTHENTICATED
          ? CartUrlValueEnum.ANONYMOUS_CART
          : CartUrlValueEnum.REGISTERED_CART_CURRENT;
    }
  }

  public getHttpHeaders(
    cartUser: CartUser,
    providedToken?: string,
  ): HttpHeaders | undefined {
    if (providedToken) {
      return new HttpHeaders({ Authorization: `Bearer ${providedToken}` });
    }

    let httpHeaders: HttpHeaders | undefined;
    if (cartUser.type !== UserEnum.B2C_AUTHENTICATED) return httpHeaders;

    return new HttpHeaders({ Authorization: `Bearer ${cartUser.b2cToken}` });
  }

  public storeLocalCart(
    baseSite: BoelsBaseSite,
    cartUserType: UserEnum,
    cartGuid: string,
    override?: 'registerdToAnonymous',
  ): void {
    if (cartUserType === UserEnum.B2C_AUTHENTICATED && !override) return;
    this.setCartId(cartUserType, cartGuid);

    const localCart: LocalCartDataType = {
      cartId: cartGuid,
      user_type: cartUserType,
    };
    this.#localStorageService.setItem(
      (this.#cartKeyPrefix + baseSite.uid) as StorageKey,
      localCart,
    );
  }

  public removeLocalCart(baseSite: BoelsBaseSite, purgeEmail?: boolean): void {
    this.#cartGuidSubject.next(undefined);
    this.#localStorageService.removeItem(
      (this.#cartKeyPrefix + baseSite.uid) as StorageKey,
    );

    if (purgeEmail) this.#localStorageService.removeItem('email' as StorageKey);
  }

  public registerLocalCartAsGuest(cartId: string, baseId: string): void {
    const localCart: LocalCartDataType = {
      cartId: cartId,
      user_type: UserEnum.GUEST,
    };
    this.#localStorageService.setItem(
      (this.#cartKeyPrefix + baseId) as StorageKey,
      localCart,
    );

    this.#cartGuidSubject.next(undefined);
    this.#store.dispatch(createCart());
  }
}
